import { useState, useCallback } from "react";
import { CreditNoteDataEntity } from "../../domain/entities/CreditNoteEntity";
import { CreateZohoCreditNoteUseCase } from "../../domain/use-cases/CreateZohoCreditNoteUseCase";
import { UpdateZohoCreditNoteUseCase } from "../../domain/use-cases/UpdateZohoCreditNoteUseCase";
import { UpdateCreditNoteJsonUseCase } from "../../domain/use-cases/UpdateCreditNoteJsonUseCase";
import { RejectCreditNoteUseCase } from "../../domain/use-cases/RejectCreditNoteUseCase";
import { ApproveCreditNoteUseCase } from "../../domain/use-cases/ApproveCreditNoteUseCase";
import { SaveCreditNoteAsDraftUseCase } from "../../domain/use-cases/SaveCreditNoteAsDraftUseCase";
import { DeleteCreditNoteUseCase } from "../../domain/use-cases/DeleteCreditNoteUseCase";
import { CreditNoteRepositoryImpl } from "../../data/repositories/CreditNoteRepositoryImpl";
import { CreditNoteEntity } from "../../domain/entities/CreditNoteEntity";

// Create repository instance (singleton)
const creditNoteRepository = new CreditNoteRepositoryImpl();

// Create use cases
const createZohoCreditNoteUseCase = new CreateZohoCreditNoteUseCase(
  creditNoteRepository
);
const updateZohoCreditNoteUseCase = new UpdateZohoCreditNoteUseCase(
  creditNoteRepository
);
const updateCreditNoteJsonUseCase = new UpdateCreditNoteJsonUseCase(
  creditNoteRepository
);
const rejectCreditNoteUseCase = new RejectCreditNoteUseCase(
  creditNoteRepository
);
const approveCreditNoteUseCase = new ApproveCreditNoteUseCase(
  creditNoteRepository
);
const saveCreditNoteAsDraftUseCase = new SaveCreditNoteAsDraftUseCase(
  creditNoteRepository
);
const deleteCreditNoteUseCase = new DeleteCreditNoteUseCase(
  creditNoteRepository
);

export interface UseCreditNoteOperationsReturn {
  loading: boolean;
  error: string | null;

  createZohoCreditNote: (
    data: CreditNoteDataEntity,
    pdfUrl?: string,
    creditNoteId?: number
  ) => Promise<boolean>;
  updateZohoCreditNote: (
    creditNoteId: number | string,
    data: CreditNoteDataEntity
  ) => Promise<boolean>;
  updateCreditNoteJson: (
    creditNoteId: number,
    data: CreditNoteDataEntity
  ) => Promise<boolean>;
  rejectCreditNote: (creditNoteId: number) => Promise<boolean>;
  approveCreditNote: (creditNoteId: number) => Promise<boolean>;
  deleteCreditNote: (creditNoteId: number | string) => Promise<boolean>;
  saveCreditNoteAsDraft: (
    data: CreditNoteDataEntity,
    options?: {
      jobid?: string | null;
      folder_id?: string | null;
      company_id?: number | null;
      gst_rates?: any;
      gst_types?: any;
      is_tds_applied?: number;
      tds_tax_id?: string | null;
      tds_tax_amount?: number | null;
      tds_tax_percentage?: number | null;
      tds_tax_name?: string | null;
      tds_section?: string | null;
    }
  ) => Promise<CreditNoteEntity | null>;

  clearError: () => void;
}

export const useCreditNoteOperations = (): UseCreditNoteOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create credit note in Zoho Books
   * If creditNoteId is provided, updates the existing credit note instead of creating a new one
   */
  const createZohoCreditNote = useCallback(
    async (
      data: CreditNoteDataEntity,
      pdfUrl?: string,
      creditNoteId?: number
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createZohoCreditNoteUseCase.execute(
          data,
          pdfUrl,
          creditNoteId
        );

        if (!result.success) {
          throw new Error(
            result.error || "Failed to create credit note in Zoho"
          );
        }

        return result.success;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to create credit note in Zoho";
        setError(errorMessage);
        console.error("Failed to create Zoho credit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update credit note in Zoho Books
   */
  const updateZohoCreditNote = useCallback(
    async (
      creditNoteId: number | string,
      data: CreditNoteDataEntity
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateZohoCreditNoteUseCase.execute(
          creditNoteId,
          data
        );

        if (!result.success) {
          throw new Error(
            result.error || "Failed to update credit note in Zoho"
          );
        }

        return result.success;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to update credit note in Zoho";
        setError(errorMessage);
        console.error("Failed to update Zoho credit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update credit note JSON data (for auto-save and manual edits)
   */
  const updateCreditNoteJson = useCallback(
    async (
      creditNoteId: number,
      data: CreditNoteDataEntity
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateCreditNoteJsonUseCase.execute(
          creditNoteId,
          data
        );

        if (!result.success) {
          throw new Error(result.error || "Failed to update credit note JSON");
        }

        return true;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to update credit note JSON";
        setError(errorMessage);
        console.error("Failed to update credit note JSON:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Reject a credit note
   */
  const rejectCreditNote = useCallback(
    async (creditNoteId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await rejectCreditNoteUseCase.execute(creditNoteId);

        if (!result.success) {
          throw new Error(result.error || "Failed to reject credit note");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to reject credit note";
        setError(errorMessage);
        console.error("Failed to reject credit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Approve a credit note
   */
  const approveCreditNote = useCallback(
    async (creditNoteId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await approveCreditNoteUseCase.execute(creditNoteId);

        if (!result.success) {
          throw new Error(result.error || "Failed to approve credit note");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to approve credit note";
        setError(errorMessage);
        console.error("Failed to approve credit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Delete credit note (unified endpoint)
   * Handles both draft (DB only) and posted (unapply + Zoho delete) credit notes
   * Accepts both internal numeric ids and Zoho ids
   */
  const deleteCreditNote = useCallback(
    async (creditNoteId: number | string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await deleteCreditNoteUseCase.execute(creditNoteId);

        if (!result.success) {
          throw new Error(result.error || "Failed to delete credit note");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to delete credit note";
        setError(errorMessage);
        console.error("Failed to delete credit note:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Save credit note as draft
   */
  const saveCreditNoteAsDraft = useCallback(
    async (
      data: CreditNoteDataEntity,
      options?: {
        jobid?: string | null;
        folder_id?: string | null;
        company_id?: number | null;
        gst_rates?: any;
        gst_types?: any;
        is_tds_applied?: number;
        tds_tax_id?: string | null;
        tds_tax_amount?: number | null;
        tds_tax_percentage?: number | null;
        tds_tax_name?: string | null;
        tds_section?: string | null;
      }
    ): Promise<CreditNoteEntity | null> => {
      setLoading(true);
      setError(null);

      try {
        const result = await saveCreditNoteAsDraftUseCase.execute(
          data,
          options
        );

        return result;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to save credit note as draft";
        setError(errorMessage);
        console.error("Failed to save credit note as draft:", error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createZohoCreditNote,
    updateZohoCreditNote,
    updateCreditNoteJson,
    rejectCreditNote,
    approveCreditNote,
    deleteCreditNote,
    saveCreditNoteAsDraft,
    clearError,
  };
};
